/**
 * Admin — Kit Reimbursement Payout Screen (Feb 2026).
 *
 * Lists every commission_ledger row where entry_type is
 * partner_kit_reimbursement (or its reversal). Admins can filter by
 * state / partner / date, approve `held_for_review` rows in a single
 * click, void with a reason, and bulk-apply either action to a
 * selected set. CSV export deep-links to the same filter set.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  DollarSign, RefreshCw, Loader2, CheckCircle2, Ban, Download,
  Filter, AlertTriangle, X, Search, Package,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { PortalShell } from "@/components/PortalShell";

const CENTS = (n) => `$${((n || 0) / 100).toFixed(2)}`;
const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Only these two states are actionable from this screen — matches the
// backend's ACTIONABLE_STATES set. All others render read-only.
const APPROVE_FROM = "held_for_review";
const VOID_FROM = new Set(["held_for_review", "eligible"]);

const STATE_LABELS = {
  held_for_review:   { label: "Held for review", tone: "bg-amber-100 text-amber-800 border-amber-200" },
  eligible:          { label: "Eligible",        tone: "bg-cyan-100 text-cyan-800 border-cyan-200" },
  payout_committed:  { label: "Committed",       tone: "bg-indigo-100 text-indigo-800 border-indigo-200" },
  paid:              { label: "Paid",            tone: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  void:              { label: "Void",            tone: "bg-slate-100 text-slate-600 border-slate-200" },
  reversed:          { label: "Reversed",        tone: "bg-rose-100 text-rose-800 border-rose-200" },
};

function StateBadge({ state }) {
  const s = STATE_LABELS[state] || { label: state || "—", tone: "bg-slate-100 text-slate-600 border-slate-200" };
  return (
    <span
      data-testid={`kit-reimb-state-${state}`}
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${s.tone}`}
    >
      {s.label}
    </span>
  );
}

export default function AdminKitReimbursements() {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState("held_for_review,eligible");
  const [partnerFilter, setPartnerFilter] = useState("");
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [voidTarget, setVoidTarget] = useState(null);   // ledger row for the void dialog
  const [voidReason, setVoidReason] = useState("");
  const [bulkVoidReason, setBulkVoidReason] = useState("");
  const [bulkVoidOpen, setBulkVoidOpen] = useState(false);

  const buildQuery = () => {
    const p = new URLSearchParams();
    if (state)         p.set("state", state);
    if (partnerFilter) p.set("partner_id", partnerFilter);
    if (dateFrom)      p.set("date_from", dateFrom);
    if (dateTo)        p.set("date_to", dateTo);
    if (q)             p.set("q", q);
    return p;
  };

  const reload = async () => {
    setLoading(true);
    setSelected(new Set());
    try {
      const { data } = await api.get(`/admin/kit-reimbursements?${buildQuery().toString()}`);
      setRows(data.rows || []);
      setTotals(data.totals_by_state || {});
    } catch {
      toast.error("Failed to load kit reimbursements.");
    } finally { setLoading(false); }
  };

  // Reload whenever the filter state changes.
  useEffect(() => { reload(); }, [state, partnerFilter, dateFrom, dateTo]);

  const grandTotalCents = useMemo(
    () => Object.values(totals).reduce((acc, t) => acc + (t.cents || 0), 0),
    [totals],
  );

  const filteredRows = useMemo(() => {
    // Free-text q is server-side, but we let the user type-then-Enter
    // rather than hitting the server on every keystroke.
    return rows;
  }, [rows]);

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    const actionable = filteredRows.filter((r) => VOID_FROM.has(r.state));
    if (selected.size === actionable.length && actionable.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(actionable.map((r) => r.id)));
    }
  };

  const approveRow = async (row) => {
    setBusy(true);
    try {
      await api.post(`/admin/kit-reimbursements/${row.id}/approve`);
      toast.success(`Approved — ${row.booking_ref} · ${CENTS(row.amount_cents)}`);
      reload();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Approve failed."));
    } finally { setBusy(false); }
  };

  const submitVoid = async () => {
    if (!voidTarget) return;
    if ((voidReason || "").trim().length < 3) {
      toast.error("Add a reason (min 3 chars)."); return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/kit-reimbursements/${voidTarget.id}/void`,
        { reason: voidReason.trim() });
      toast.success(`Voided — ${voidTarget.booking_ref}`);
      setVoidTarget(null); setVoidReason("");
      reload();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Void failed."));
    } finally { setBusy(false); }
  };

  const bulkApprove = async () => {
    if (selected.size === 0) { toast.error("Select at least one row."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/kit-reimbursements/bulk", {
        ids:    Array.from(selected),
        action: "approve",
      });
      toast.success(`Approved ${data.processed} of ${data.total}.`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk approve failed.");
    } finally { setBusy(false); }
  };

  const bulkVoid = async () => {
    if ((bulkVoidReason || "").trim().length < 3) {
      toast.error("Add a reason (min 3 chars)."); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/kit-reimbursements/bulk", {
        ids:    Array.from(selected),
        action: "void",
        reason: bulkVoidReason.trim(),
      });
      toast.success(`Voided ${data.processed} of ${data.total}.`);
      setBulkVoidOpen(false); setBulkVoidReason("");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk void failed.");
    } finally { setBusy(false); }
  };

  const exportCsv = () => {
    const url = `${API_BASE}/api/admin/kit-reimbursements/export.csv?${buildQuery().toString()}`;
    window.open(url, "_blank");
  };

  return (
    <PortalShell>
      <div className="p-6 space-y-4" data-testid="admin-kit-reimbursements-page">
        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <DollarSign className="w-6 h-6" /> Kit Reimbursements
            </h1>
            <p className="text-slate-600 text-sm mt-1">
              Partner-supplied kit refund ledger — review, approve for
              payout, or void with a reason. Reversal rows for already-paid
              reimbursements appear read-only.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/admin/kit-reimbursement-payout-runs"
              data-testid="admin-kit-reimb-payout-runs-link"
              className="inline-flex items-center gap-1.5 rounded-lg border border-cyan-600 bg-white text-cyan-700 hover:bg-cyan-50 text-sm font-semibold px-3 py-1.5 transition-colors"
            >
              <Package className="w-3.5 h-3.5" /> Payout Runs &rarr;
            </Link>
            <Button
              type="button"
              onClick={reload}
              variant="outline" size="sm"
              data-testid="admin-kit-reimb-refresh"
              className="gap-1.5"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button
              type="button"
              onClick={exportCsv}
              variant="outline" size="sm"
              data-testid="admin-kit-reimb-export-csv"
              className="gap-1.5"
            >
              <Download className="w-3.5 h-3.5" /> Export CSV
            </Button>
          </div>
        </div>

        {/* Roll-up tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-kit-reimb-totals">
          {["held_for_review", "eligible", "paid", "void"].map((s) => {
            const t = totals[s] || { count: 0, cents: 0 };
            const cfg = STATE_LABELS[s];
            return (
              <div
                key={s}
                data-testid={`admin-kit-reimb-total-${s}`}
                className={`p-3 rounded-xl border ${cfg.tone}`}
              >
                <div className="text-[10px] uppercase tracking-wider font-bold opacity-80">{cfg.label}</div>
                <div className="text-xl font-bold mt-0.5 tabular-nums">
                  {t.count} <span className="text-xs font-normal opacity-70">· {CENTS(t.cents)}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Filters */}
        <div className="rounded-xl border border-slate-200 bg-white p-3 flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1">State</label>
            <select
              data-testid="admin-kit-reimb-filter-state"
              value={state}
              onChange={(e) => setState(e.target.value)}
              className="text-sm px-2 py-1.5 rounded border border-slate-200"
            >
              <option value="">All</option>
              <option value="held_for_review">Held for review</option>
              <option value="eligible">Eligible</option>
              <option value="held_for_review,eligible">Held + Eligible (actionable)</option>
              <option value="paid">Paid</option>
              <option value="void">Void</option>
              <option value="reversed">Reversed</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1">Partner ID</label>
            <input
              data-testid="admin-kit-reimb-filter-partner"
              value={partnerFilter}
              onChange={(e) => setPartnerFilter(e.target.value)}
              placeholder="paste id"
              className="text-sm px-2 py-1.5 rounded border border-slate-200 w-40"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1">Date from</label>
            <input
              type="date"
              data-testid="admin-kit-reimb-filter-date-from"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="text-sm px-2 py-1.5 rounded border border-slate-200"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1">Date to</label>
            <input
              type="date"
              data-testid="admin-kit-reimb-filter-date-to"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="text-sm px-2 py-1.5 rounded border border-slate-200"
            />
          </div>
          <form
            onSubmit={(e) => { e.preventDefault(); reload(); }}
            className="flex items-end gap-1"
          >
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1">Search</label>
              <input
                data-testid="admin-kit-reimb-filter-q"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="booking ref, description…"
                className="text-sm px-2 py-1.5 rounded border border-slate-200 w-64"
              />
            </div>
            <Button type="submit" variant="outline" size="sm" data-testid="admin-kit-reimb-filter-submit">
              <Search className="w-3.5 h-3.5" />
            </Button>
          </form>
        </div>

        {/* Bulk actions bar (only when selection exists) */}
        {selected.size > 0 && (
          <div
            data-testid="admin-kit-reimb-bulk-bar"
            className="rounded-xl border border-cyan-300 bg-cyan-50 px-4 py-2 flex items-center gap-3"
          >
            <div className="text-sm font-semibold text-cyan-900 flex-1">
              {selected.size} row{selected.size === 1 ? "" : "s"} selected
            </div>
            <Button
              type="button"
              size="sm"
              disabled={busy}
              onClick={bulkApprove}
              data-testid="admin-kit-reimb-bulk-approve"
              className="gap-1.5 bg-emerald-600 hover:bg-emerald-700"
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> Approve selected
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => setBulkVoidOpen(true)}
              data-testid="admin-kit-reimb-bulk-void"
              className="gap-1.5 border-rose-300 text-rose-700 hover:bg-rose-50"
            >
              <Ban className="w-3.5 h-3.5" /> Void selected
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setSelected(new Set())}
              data-testid="admin-kit-reimb-bulk-clear"
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}

        {/* Table */}
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="admin-kit-reimb-table">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left w-8">
                    <input
                      type="checkbox"
                      data-testid="admin-kit-reimb-select-all"
                      checked={selected.size > 0 && selected.size === filteredRows.filter((r) => VOID_FROM.has(r.state)).length}
                      onChange={toggleAll}
                    />
                  </th>
                  <th className="px-3 py-2 text-left">Booking</th>
                  <th className="px-3 py-2 text-left">Partner</th>
                  <th className="px-3 py-2 text-left">State</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2 text-left">Payment</th>
                  <th className="px-3 py-2 text-left">Created</th>
                  <th className="px-3 py-2 text-left">Actor</th>
                  <th className="px-3 py-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && (
                  <tr><td colSpan={9} className="py-8 text-center text-slate-500">
                    <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
                  </td></tr>
                )}
                {!loading && filteredRows.length === 0 && (
                  <tr><td colSpan={9} className="py-10 text-center text-slate-500">
                    No kit reimbursements match the current filter.
                  </td></tr>
                )}
                {!loading && filteredRows.map((r) => {
                  const canApprove = r.state === APPROVE_FROM;
                  const canVoid = VOID_FROM.has(r.state);
                  const isReversal = r.entry_type === "partner_kit_reimbursement_reversal";
                  return (
                    <tr
                      key={r.id}
                      data-testid={`admin-kit-reimb-row-${r.id}`}
                      className="hover:bg-slate-50/50 transition-colors"
                    >
                      <td className="px-3 py-2 align-top">
                        <input
                          type="checkbox"
                          data-testid={`admin-kit-reimb-select-${r.id}`}
                          disabled={!canVoid}
                          checked={selected.has(r.id)}
                          onChange={() => toggleOne(r.id)}
                        />
                      </td>
                      <td className="px-3 py-2 align-top">
                        <div className="font-mono text-xs font-semibold">{r.booking_ref || "—"}</div>
                        {isReversal && (
                          <div className="text-[10px] text-rose-700 mt-0.5 uppercase font-bold tracking-wider">
                            Reversal row
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <div className="text-xs font-semibold text-slate-900">
                          {r.partner?.business_name || <span className="text-slate-400">— (no partner)</span>}
                        </div>
                        <div className="text-[10px] font-mono text-slate-500">{r.partner?.partner_slug || ""}</div>
                      </td>
                      <td className="px-3 py-2 align-top"><StateBadge state={r.state} /></td>
                      <td className={`px-3 py-2 align-top text-right font-mono font-semibold tabular-nums ${(r.amount_cents || 0) < 0 ? "text-rose-700" : "text-slate-900"}`}>
                        {CENTS(r.amount_cents)}
                      </td>
                      <td className="px-3 py-2 align-top text-xs text-slate-600">{r.payment_source || "—"}</td>
                      <td className="px-3 py-2 align-top text-xs text-slate-600">
                        {r.created_at ? new Date(r.created_at).toLocaleDateString("en-AU") : "—"}
                      </td>
                      <td className="px-3 py-2 align-top text-xs text-slate-500">{r.actor_email || "—"}</td>
                      <td className="px-3 py-2 align-top">
                        <div className="flex flex-wrap gap-1.5">
                          {canApprove && (
                            <Button
                              type="button"
                              size="sm"
                              disabled={busy}
                              onClick={() => approveRow(r)}
                              data-testid={`admin-kit-reimb-approve-${r.id}`}
                              className="gap-1 bg-emerald-600 hover:bg-emerald-700 text-[11px] h-7"
                            >
                              <CheckCircle2 className="w-3 h-3" /> Approve
                            </Button>
                          )}
                          {canVoid && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={busy}
                              onClick={() => { setVoidTarget(r); setVoidReason(""); }}
                              data-testid={`admin-kit-reimb-void-${r.id}`}
                              className="gap-1 border-rose-300 text-rose-700 hover:bg-rose-50 text-[11px] h-7"
                            >
                              <Ban className="w-3 h-3" /> Void
                            </Button>
                          )}
                          {!canApprove && !canVoid && (
                            <span className="text-[10px] text-slate-400 italic">no actions</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Grand total footer */}
        <div className="text-right text-xs text-slate-500">
          Grand total (all shown states): <span className="font-mono font-bold text-slate-800">{CENTS(grandTotalCents)}</span>
        </div>
      </div>

      {/* Single-row void dialog */}
      {voidTarget && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => { if (!busy) { setVoidTarget(null); setVoidReason(""); } }}
          data-testid="admin-kit-reimb-void-modal"
        >
          <div
            className="bg-white rounded-2xl p-5 w-full max-w-md shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-rose-600" />
              <h3 className="text-lg font-bold text-slate-900">Void reimbursement</h3>
            </div>
            <div className="text-xs text-slate-600 mb-3">
              <div><strong>Booking:</strong> {voidTarget.booking_ref}</div>
              <div><strong>Partner:</strong> {voidTarget.partner?.business_name || "—"}</div>
              <div><strong>Amount:</strong> {CENTS(voidTarget.amount_cents)}</div>
            </div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1">Reason (min 3 chars)</label>
            <textarea
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              data-testid="admin-kit-reimb-void-reason"
              rows={3}
              placeholder="e.g. customer confirmed they received the kit via mail-out — reimbursement not owed."
              className="w-full text-sm p-2 border border-slate-200 rounded"
            />
            <div className="flex gap-2 justify-end mt-3">
              <Button
                type="button" variant="outline" size="sm"
                disabled={busy}
                onClick={() => { setVoidTarget(null); setVoidReason(""); }}
                data-testid="admin-kit-reimb-void-cancel"
              >Cancel</Button>
              <Button
                type="button" size="sm"
                disabled={busy}
                onClick={submitVoid}
                data-testid="admin-kit-reimb-void-submit"
                className="bg-rose-600 hover:bg-rose-700 gap-1"
              >
                <Ban className="w-3 h-3" /> Void reimbursement
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk-void dialog */}
      {bulkVoidOpen && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => { if (!busy) { setBulkVoidOpen(false); setBulkVoidReason(""); } }}
          data-testid="admin-kit-reimb-bulk-void-modal"
        >
          <div
            className="bg-white rounded-2xl p-5 w-full max-w-md shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-rose-600" />
              <h3 className="text-lg font-bold text-slate-900">
                Void {selected.size} reimbursement{selected.size === 1 ? "" : "s"}
              </h3>
            </div>
            <div className="text-xs text-slate-600 mb-3">
              This reason will be stamped onto every selected row. Rows
              already in a non-actionable state will be skipped.
            </div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1">Reason (min 3 chars)</label>
            <textarea
              value={bulkVoidReason}
              onChange={(e) => setBulkVoidReason(e.target.value)}
              data-testid="admin-kit-reimb-bulk-void-reason"
              rows={3}
              className="w-full text-sm p-2 border border-slate-200 rounded"
            />
            <div className="flex gap-2 justify-end mt-3">
              <Button
                type="button" variant="outline" size="sm"
                disabled={busy}
                onClick={() => { setBulkVoidOpen(false); setBulkVoidReason(""); }}
                data-testid="admin-kit-reimb-bulk-void-cancel"
              >Cancel</Button>
              <Button
                type="button" size="sm"
                disabled={busy}
                onClick={bulkVoid}
                data-testid="admin-kit-reimb-bulk-void-submit"
                className="bg-rose-600 hover:bg-rose-700 gap-1"
              >
                <Ban className="w-3 h-3" /> Void all
              </Button>
            </div>
          </div>
        </div>
      )}
    </PortalShell>
  );
}
